import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatPseudoCheckboxModule } from '@angular/material/core';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatMenu, MatMenuModule } from '@angular/material/menu';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { AgentStatus, ExtensionQuery, Me, MeQuery, MeService, SystemStatusCode } from '@b3networks/api/callcenter';
import { StateIconFrameComponent } from '@b3networks/chat/shared/core';
import { BusyNoteComponent } from '@b3networks/comms/callcenter/shared';
import { DestroySubscriberComponent } from '@b3networks/shared/common';
import { ThemeService } from '@b3networks/shared/ui/theme';
import { ToastService } from '@b3networks/shared/ui/toast';
import { filter, takeUntil } from 'rxjs';

@Component({
  selector: 'b3n-app-status-menu',
  templateUrl: './app-status-menu.component.html',
  styleUrls: ['./app-status-menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    StateIconFrameComponent,
    MatPseudoCheckboxModule,
    MatMenuModule,
    MatDialogModule,
    MatSelectModule,
    MatFormFieldModule,
    FormsModule,
    MatButtonModule,
    MatTooltipModule
  ]
})
export class AppStatusMenuComponent extends DestroySubscriberComponent implements OnInit {
  @ViewChild('agentStatus', { static: true }) agentStatus: MatMenu;
  isDarkMode$ = this.themeService.isDarkMode$;

  @Input() meCallcenter: Me;

  @Output() acwCall = new EventEmitter<Me>();
  @Output() endCall = new EventEmitter<Me>();

  readonly SystemStatusCode = SystemStatusCode;
  readonly AgentStatus = AgentStatus;

  constructor(
    private meQuery: MeQuery,
    private meService: MeService,
    private toastr: ToastService,
    private dialog: MatDialog,
    private themeService: ThemeService,
    private extensionQuery: ExtensionQuery
  ) {
    super();
  }

  ngOnInit() {}

  changeStatus(status: AgentStatus, currentStatus: AgentStatus) {
    if (status === currentStatus && status !== AgentStatus.busy) {
      return;
    }

    switch (status) {
      case AgentStatus.available:
        this.login();
        break;
      case AgentStatus.busy:
        this.busy();
        break;
      case AgentStatus.offline:
        this.logout();
        break;
      case AgentStatus.dnd:
        this.selectDND();
        break;
    }
  }

  private login() {
    this.meService.login(null).subscribe(
      _ => {
        this.toastr.success('Updated status successfully');
      },
      err => {
        this.toastr.error(err.message);
      }
    );
  }

  private selectDND() {
    this.meService.dnd().subscribe(
      _ => {
        this.toastr.success('Updated status successfully');
      },
      err => {
        this.toastr.error(err.message);
      }
    );
  }

  private logout() {
    this.meService.logout().subscribe(
      _ => {
        this.toastr.success('Updated status successfully');
      },
      err => {
        this.toastr.error(err.message);
      }
    );
  }

  private busy() {
    this.extensionQuery.me$
      .pipe(
        filter(me => me != null),
        takeUntil(this.destroySubscriber$)
      )
      .subscribe(extension => {
        if (extension.isCallcenter) {
          this.dialog
            .open(BusyNoteComponent, { panelClass: this.themeService.isDarkMode ? 'dark-theme-portal-base' : null })
            .afterClosed()
            .subscribe(result => {
              if (result && result?.reason) {
                const me = this.meQuery.getValue()?.me;
                if (me?.status === AgentStatus.busy && me?.busyReason === result?.reason) {
                  return;
                }
                this.makeBusy(result.reason);
              }
            });
        } else {
          this.makeBusy(null);
        }
      });
  }

  private makeBusy(reason: string) {
    this.meService.makeBusy(reason).subscribe({
      next: () => {
        this.toastr.success('Updated status successfully');
      },
      error: err => {
        this.toastr.error(err.message);
      }
    });
  }
}
